import React from 'react';
import { motion } from 'framer-motion';
import {
  Star,
  MessageCircle,
  Bot,
  AlertTriangle,
  Send,
  Receipt,
  Clock3,
  FileBarChart,
  ArrowRight,
  Zap,
} from 'lucide-react';
import { MakeMark, N8nMark, GoogleMark, YelpMark, TripAdvisorMark } from '../BrandIcons';

/* Review Automation / Custom AI Automation — concrete Make & n8n
   workflow pipelines a restaurant operator can immediately picture. */

const Grow = ({ children, i = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-50px' }}
    transition={{ duration: 0.5, delay: i * 0.1 }}
    className={className}
  >
    {children}
  </motion.div>
);

function Node({ icon: Icon, label, tone = 'default' }) {
  const tones = {
    default: 'bg-white/[0.05] border-white/12 text-neutral-200',
    trigger: 'bg-sky-400/10 border-sky-400/30 text-sky-200',
    ai: 'bg-[#F77F11]/12 border-[#F77F11]/35 text-[#FFD9B3]',
    win: 'bg-emerald-400/10 border-emerald-400/30 text-emerald-200',
    alert: 'bg-red-400/10 border-red-400/30 text-red-200',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11.5px] font-semibold leading-tight ${tones[tone]}`}>
      <Icon className="w-3.5 h-3.5 shrink-0" />
      {label}
    </span>
  );
}

const Arrow = () => <ArrowRight className="w-3.5 h-3.5 text-neutral-600 shrink-0" />;

export default function AutomationShowcase({ lang }) {
  const es = lang === 'es';

  const flows = [
    {
      Mark: MakeMark,
      platform: 'Make',
      title: es ? 'Respuesta inteligente a cada reseña' : 'Smart response to every review',
      saves: es ? 'Ahorra ~6 h/semana' : 'Saves ~6 hr/week',
      nodes: [
        { icon: Star, label: es ? 'Reseña nueva en Google / Yelp' : 'New Google / Yelp review', tone: 'trigger' },
        { icon: Bot, label: es ? 'IA clasifica el sentimiento' : 'AI classifies sentiment', tone: 'ai' },
        { icon: Bot, label: es ? 'Redacta con tu voz de marca' : 'Drafts in your brand voice', tone: 'ai' },
        { icon: Send, label: es ? '≥4★ → se publica sola' : '≥4★ → auto-published', tone: 'win' },
        { icon: AlertTriangle, label: es ? '≤3★ → alerta WhatsApp al gerente' : '≤3★ → WhatsApp alert to manager', tone: 'alert' },
      ],
      note: es
        ? 'Las reseñas negativas nunca se publican solas: activan un flujo de recuperación con tu equipo.'
        : 'Negative reviews are never auto-published: they trigger a recovery flow with your team.',
    },
    {
      Mark: N8nMark,
      platform: 'n8n',
      title: es ? 'Pedir la reseña en el momento exacto' : 'Ask for the review at the exact right moment',
      saves: es ? '+40% de reseñas nuevas' : '+40% new reviews',
      nodes: [
        { icon: Receipt, label: es ? 'El POS cierra la mesa' : 'POS closes the table', tone: 'trigger' },
        { icon: Clock3, label: es ? 'Espera 90 minutos' : 'Waits 90 minutes', tone: 'default' },
        { icon: MessageCircle, label: es ? 'SMS con link directo a Google' : 'SMS with direct Google link', tone: 'ai' },
        { icon: Star, label: es ? 'Reseña registrada en tu CRM' : 'Review logged in your CRM', tone: 'win' },
      ],
      note: es
        ? 'El momento óptimo para pedir una reseña es 90 min después de la visita — automatizado, nunca se olvida.'
        : 'The optimal moment to ask for a review is 90 min post-visit — automated, never forgotten.',
    },
    {
      Mark: N8nMark,
      platform: 'n8n + Make',
      title: es ? 'Reporte semanal de reputación al owner' : 'Weekly reputation digest for the owner',
      saves: es ? 'Cero hojas de cálculo' : 'Zero spreadsheets',
      nodes: [
        { icon: Clock3, label: es ? 'Cada lunes 8:00 AM' : 'Every Monday 8:00 AM', tone: 'trigger' },
        { icon: Star, label: es ? 'Compila Google, Yelp y TripAdvisor' : 'Compiles Google, Yelp & TripAdvisor', tone: 'default' },
        { icon: Bot, label: es ? 'IA resume temas recurrentes' : 'AI summarizes recurring themes', tone: 'ai' },
        { icon: FileBarChart, label: es ? 'Reporte a tu email / Slack' : 'Report to your email / Slack', tone: 'win' },
      ],
      note: es
        ? '"El servicio del sábado fue lento" deja de ser anécdota y se convierte en un dato accionable.'
        : '"Saturday service was slow" stops being an anecdote and becomes an actionable data point.',
    },
  ];

  return (
    <section className="py-20 lg:py-24 bg-neutral-950 text-white relative overflow-hidden" data-testid="showcase-automation">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 left-[15%] w-[600px] h-[420px] rounded-full blur-3xl opacity-[0.13]"
        style={{ background: 'radial-gradient(circle, #F77F11 0%, transparent 65%)' }}
      />
      <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <p className="kx-eyebrow-dark">{es ? 'Automatizaciones reales' : 'Real automations'}</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-[-0.02em] leading-tight">
            {es ? 'Automatizaciones para tu ecosistema' : 'Automations for your ecosystem'}
          </h2>
          <p className="mt-4 text-neutral-400 text-[15.5px] leading-relaxed">
            {es
              ? 'No vendemos "automatización" en abstracto. KITXENS funciona con su propia IA nativa — y cuando tu restaurante ya usa n8n, Make, Zapier u otras plataformas, nos integramos con tu ecosistema y construimos flujos concretos sobre él, operados por nosotros mes a mes.'
              : "We don't sell 'automation' in the abstract. KITXENS runs on its own native AI — and when your restaurant already uses n8n, Make, Zapier or other platforms, we integrate with your ecosystem and build concrete workflows on top of it, operated by us month over month."}
          </p>
          <div className="mt-5 flex items-center gap-2">
            <span className="text-[11px] tracking-[0.18em] uppercase font-bold text-neutral-500">{es ? 'Nos integramos con' : 'We integrate with'}</span>
            <MakeMark className="h-6 px-2 text-[11px]" />
            <N8nMark className="h-6 px-2 text-[11px]" />
            <GoogleMark className="w-6 h-6" />
            <YelpMark className="w-6 h-6" />
            <TripAdvisorMark className="w-6 h-6" />
          </div>
        </div>

        <div className="mt-12 space-y-5">
          {flows.map((f, i) => (
            <Grow key={f.title} i={i}>
              <div className="rounded-3xl bg-white/[0.04] border border-white/10 p-6 sm:p-7 hover:border-[#F77F11]/40 transition-colors">
                <div className="flex flex-wrap items-center gap-3">
                  <f.Mark className="h-6 px-2 text-[11px]" />
                  <h3 className="font-bold text-[16.5px] tracking-[-0.005em]">{f.title}</h3>
                  <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-emerald-400/10 border border-emerald-400/25 text-emerald-300 text-[11px] font-bold px-3 py-1">
                    <Zap className="w-3 h-3" /> {f.saves}
                  </span>
                </div>
                <div className="mt-5 flex flex-wrap items-center gap-2.5">
                  {f.nodes.map((n, j) => (
                    <React.Fragment key={j}>
                      {j > 0 && <Arrow />}
                      <Node {...n} />
                    </React.Fragment>
                  ))}
                </div>
                <p className="mt-4 text-[12.5px] text-neutral-500 leading-relaxed">{f.note}</p>
              </div>
            </Grow>
          ))}
        </div>

        <Grow i={3}>
          <p className="mt-8 text-[13.5px] text-neutral-400 leading-relaxed max-w-2xl">
            {es
              ? '¿Tienes otro proceso repetitivo? Inventario, facturas de proveedores, nómina de propinas, confirmación de catering — si sucede más de una vez por semana, lo podemos automatizar.'
              : 'Have another repetitive process? Inventory, supplier invoices, tip payroll, catering confirmations — if it happens more than once a week, we can automate it.'}
          </p>
        </Grow>
      </div>
    </section>
  );
}
